import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, FilterDescriptor, State } from '@progress/kendo-data-query';
import { SupplierLookupResponseModel } from './models/supplier-lookup-response-model';

@Injectable({
  providedIn: 'root',
})
export class SupplierLookupAPIService extends BehaviorSubject<GridDataResult> {
    public baseUrlLocal: string = environment.apiBaseUrl;
    public loading: boolean = false;

    constructor(protected http: HttpClient) {
        super({ data: [], total: null });
    }

    public query(state: State, filter: CompositeFilterDescriptor): void {
        this.loading = true;
        this.fetch(state, filter).subscribe((x) => super.next(x));
    }

    protected fetch(state: State, filter: CompositeFilterDescriptor): Observable<GridDataResult> {
        let queryStart = '';
        let queryString = '';

        queryStart = this.getQueryStart(queryStart);
        queryString = queryString.concat(`${queryStart}pageindex=${state.skip}`);
        queryStart = this.getQueryStart(queryStart);
        queryString = queryString.concat(`${queryStart}pagesize=${state.take}`);

        const siteIdFilter = this.getStateFilterValue(filter, 'siteId');
        if (siteIdFilter != undefined && siteIdFilter.length >= 3) {
            queryString = queryString.concat(`${queryStart}siteId=${encodeURIComponent(siteIdFilter)}`);
        }

        const supplierIdFilter = this.getStateFilterValue(filter, 'supplierId');
        if (supplierIdFilter != undefined && supplierIdFilter.length >= 3) {
            queryString = queryString.concat(`${queryStart}supplierId=${encodeURIComponent(supplierIdFilter)}`);
        }

        const siteNameFilter = this.getStateFilterValue(filter, 'siteName');
        if (siteNameFilter != undefined && siteNameFilter.length >= 3) {
            queryString = queryString.concat(`${queryStart}siteName=${encodeURIComponent(siteNameFilter)}`);
        }

        return this.http.get<GridDataResult>(this.baseUrlLocal + `supplierlookup/search` + queryString)
          .pipe(
              map(
              (response: GridDataResult) => {
                  return {
                      data: response.data,
                      total: response.total
                  };
              }
              ), tap(() => (this.loading = false))
          );
    }

    private getQueryStart(queryString: string): string {
        return queryString === '' ? '?' : '&';
    }

    private getStateFilterValue(filter: CompositeFilterDescriptor, field: string): string | undefined {
        if (!filter?.filters?.length) {
            return undefined;
        }
        const matchingFilter = filter.filters.find((item): item is FilterDescriptor => {
            return !('filters' in item) && item.field === field;
        });
        if (matchingFilter?.value === undefined || matchingFilter.value === null) {
            return undefined;
        }
        return String(matchingFilter.value);
    }
}

@Injectable()
export class SupplierLookupService extends SupplierLookupAPIService {
    constructor(http: HttpClient) {
        super(http);
    }

    public create(model: SupplierLookupResponseModel): Observable<SupplierLookupResponseModel> {
        return this.http.post<SupplierLookupResponseModel>(this.baseUrlLocal + `supplierlookup`, model);
    }

    public update(siteId: string, supplierId: string, model: SupplierLookupResponseModel): Observable<SupplierLookupResponseModel> {
        return this.http.put<SupplierLookupResponseModel>(this.baseUrlLocal + `supplierlookup/${siteId}/${supplierId}`, model);
    }

    public delete(siteId: string, supplierId: string): Observable<void> {
        return this.http.delete<void>(this.baseUrlLocal + `supplierlookup/${siteId}/${supplierId}`);
    }
}
